import { MessagePool } from 'store/findProvider/types';
import { ProviderHospital, ProviderLanguage } from 'store/providerSearch/types';
import { RecentSearchParams } from 'storage/recentSearches/common';

export interface ProviderSummary {
  score: number;
  providerId: string;
  corpProvId: number;
  locationId: number;
  ncid: string;
  npi: number;
  cernerResourceId: number;
  cernerPersonnelId: number;
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  displayName: string;
  gender: string;
  rating: number | null;
  specialties: Specialty[];
  primarySpecialty: Specialty;
  locations: ProviderLocation[];
  primaryLocation?: ProviderLocation;
  profileImage: string;
  isSelectHealth?: 'Y' | 'N' | null;
  isICentraPcp: boolean;
  messagingPools: MessagePool[];
  providerProfileImageUrl: string;
  providerProfileVideoId: string | null;
  primaryHospitalAffiliation: ProviderHospital | null;
  city: string;
  hasOnlineNpvSlots: boolean;
  isAcceptingOnlineBooking: boolean;
  isAcceptingMyInsurance: boolean;
}

export interface ProviderLocation {
  id: number;
  name: string;
  alternativeName: string | null;
  phoneNumber: string;
  address: {
    addressLine1: string;
    addressLine2: string | null;
    city: string;
    state: string;
    postalCode: string;
    country: string | null;
    isPrimary: boolean;
    latitude: number;
    longitude: number;
  };
  distance: string;
  inNetwork: boolean;
  hours: {
    is24Hours: boolean | null;
    sunday: string | null;
    monday: string | null;
    tuesday: string | null;
    wednesday: string | null;
    thursday: string | null;
    friday: string | null;
    saturday: string | null;
  };
  insurances: InsuranceSummary[];
  languages: ProviderLanguage[];
  profileImage: string;
}

export interface InsuranceSummary {
  id: string;
  name: string;
  hasOnlineNpvSlots: boolean;
  exceptions: string | null;
  aliasVal: string | null;
  primaryLocation: Nullable<Location>;
  primarySpecialty: Specialty;
  messagingPools: MessagePool[];
}

export interface Specialty {
  specialtyId: string;
  specialtyDesc: string;
  specialtyName: string;
  shortSpecialtyName: string;
  isPrimary: boolean;
}

export interface OptionType {
  label: string;
  value: string;
}

export enum ProviderFilterTitles {
  main = 'Main',
  acceptsMyInsurance = 'Accepts My Insurance',
  insurancePlans = 'Insurance Plans',
  hasOnlineNpvSlots = 'Accepts New Patients',
  isAcceptingOnlineBooking = 'Offers Online Booking',
  acceptsChildren = 'Sees Children',
  locationNearMe = 'Location Near Me',
  location = 'Location',
  locationManual = 'Enter zip code or city',
  locationAuto = 'Use current location',
  distanceRange = 'Distance Range',
  distanceRangeSm = 'Within 5 mi',
  distanceRangeMd = 'Within 10 mi',
  distanceRangeLg = 'Within 25 mi',
  distanceRangeAny = 'Any Distance',
  specialties = 'Specialties',
  gender = "Doctor's Gender",
  genderFemale = 'Female',
  genderMale = 'Male',
  hasPhotoVideo = 'Has Photo/Video',
  hasPhoto = 'Has Photo',
  hasAboutVideo = 'Has Video',
  organizationAffiliations = 'Organization Affiliations',
  stLukes = "St. Luke's",
  myHealth = 'MyHealth',
  medicalGroup = 'Intermountain Health Medical Group',
  affiliatedHospitals = 'Hospital Affiliations',
  clinics = 'Clinics',
  languagesSpoken = 'Languages Spoken'
}

export enum ProviderFilterGroups {
  hasOnlineNpvSlots = 'hasOnlineNpvSlots', // Filter providers that accept new patients
  isAcceptingOnlineBooking = 'isAcceptingOnlineBooking', // Filter providers that offer video visits
  acceptsChildren = 'acceptsChildren', // Filter providers that accept children visits
  hasPhoto = 'hasPhoto', // Filter providers that have a photo
  hasAboutVideo = 'hasAboutVideo', // Filter providers that has an about video
  isStLukes = 'isStLukes', // Filter providers that are a St Lukes affiliation
  isMyHealth = 'isMyHealth', // Filter providers that are a My Health affiliation
  isMedicalGroup = 'isMedicalGroup', // Filter providers that are Intermountain Medical Group health affiliation
  isSelectHealth = 'isSelectHealth', // Filter providers that are Select Health
  isInterMountain = 'isInterMountain' // Filter providers that are Intermountain
}

export enum ProviderFacetGroups {
  intermountainClinics = 'intermountainClinics', // Filter providers by Clinic using location id
  specialty = 'specialty', // Filter providers by specialty
  gender = 'gender', // Filter providers by gender.
  language = 'language', // Filter providers by spoken language
  rating = 'rating', // Filter providers by their rating. Results in providers greater than or equal than the provided value.
  insurancePlanNcid = 'insurancePlanNcid', // Filter providers by Insurance Plan Ncid
  hospitalAffiliationList = 'hospitalAffiliationList', // Filter providers by Hospital affiliation location id
  locationDistance = 'locationDistance', // Filter providers by location distance (in miles). This uses either the location coordinates or locationText to determine distance
  insuranceAliasValues = 'insuranceAliasValues', // Filter providers that accept user's current insurance. Mostly a usecase for front-end app.
  yearsInPractice = 'yearsInPractice' // Filter providers by their years in practice. Results in providers greater than or equal than the provided value.
}

export interface Location {
  lat: string | number;
  lng: string | number;
}

export interface AzureFacet {
  title?: string;
  group: ProviderFacetGroups;
  value: string | number;
}

export interface AzureFilter {
  title?: string;
  group: ProviderFilterGroups;
  value: boolean;
}

export interface ProviderSearchPayload {
  input?: string;
  options: ProviderSearchOptions;
  actionType?: string;
}

export interface ProviderSearchOptions {
  page?: number;
  limit?: number;
  orderBy?: string;
  input?: string;
  locationText?: string;
  filters?: AzureFilter[];
  facets?: AzureFacet[];
}

export interface ProviderSearchSuggestion {
  displayName?: string;
  providerId?: string;
  locationId?: string;
  value?: string;
  onClick?: () => void;
  disabled?: boolean;
  [key: string]: any;
}

export interface ProviderSearchRequest {
  page: number;
  limit: number;
  orderBy?: string;
  fuzzy?: boolean;
  proximity?: number;
  input?: string;
  locationText?: string;
  userInsuranceAliasValues?: string[];
  returnFields?: string[];
  userInsurance?: string;
  myPCPList?: number[];
  myPreferredProvidersList?: number[];
  myPastProvidersList?: number[];
  location?: Location;
  filters?: AzureFilter[];
  facets?: AzureFacet[];
}

export interface SearchSuggestionsPayload {
  input: string;
}

export const ID_TYPE_CORPPROV = 'corpprovid';
